import React, { useCallback } from 'react';

import { LANDING_PAGE_ROLES } from 'constants/index';

import { PDFDataContextProvider } from './context';
import { useAllPagesReady, usePrint } from './functions';
import s from './index.module.css';
import SafariCosts from './Pages/CostsPage';
import CoverPage from './Pages/CoverPage';
import EmergencyContactsPage from './Pages/EmergencyContactsPage';
import MapPage from './Pages/MapPage';
import OtherPages from './Pages/OtherPages';
import { TravelBriefPage } from './Pages/TravelBriefPage';
import { IPDFPreviewV2Config, IPDFPreviewV2Props } from './types';

const defaultConfig: IPDFPreviewV2Config = {
  showMapPage: true,
  showTravelBriefPage: true,
  showCostsPage: true,
};

function PDFPreviewV2({ onReady, config = defaultConfig, data, showImages }: IPDFPreviewV2Props) {
  config = { ...defaultConfig, ...config };

  const { canvasRef, numeratePages, allImagesLoaded } = usePrint();
  const handleAllPagesReady = useCallback(async () => {
    console.log('---- handleAllPagesReady v2 start');
    try {
      numeratePages();
      console.log('---- handleAllPagesReady v2 numeratePages finished');
      await allImagesLoaded();
      console.log('---- handleAllPagesReady v2 allImagesLoaded finished');
      onReady();
      console.log('---- handleAllPagesReady v2 onReady finished');
    } catch (e) {
      console.log('---- handleAllPagesReady v2 e', e);
    }
  }, [numeratePages, onReady, allImagesLoaded]);
  const {
    setCoverReady,
    setPagesAfterCoverPageReady,
    setMapReady,
    setTravelBriefReady,
    setPagesReady,
    setPagesAfterCostsPageReady,
    setCostsReady,
    setEmergencyContactsReady,
  } = useAllPagesReady({ config, onReady: handleAllPagesReady });

  return (
    <PDFDataContextProvider data={data} showImages={showImages}>
      <div className={s.container} ref={canvasRef}>
        <CoverPage onReady={setCoverReady} />
        <OtherPages
          pagesFilterFn={(p) =>
            [LANDING_PAGE_ROLES.ABOUT_SAFARI, LANDING_PAGE_ROLES.COMMON_PAGES_FIRST].includes(p.role)
          }
          onReady={setPagesAfterCoverPageReady}
        />
        {config.showTravelBriefPage && <TravelBriefPage onReady={setTravelBriefReady} />}
        <EmergencyContactsPage onReady={setEmergencyContactsReady} />
        {config.showMapPage && <MapPage onReady={setMapReady} />}
        <OtherPages
          pagesFilterFn={(p) =>
            [
              LANDING_PAGE_ROLES.DAY,
              LANDING_PAGE_ROLES.DETAILS,
              LANDING_PAGE_ROLES.COMMON,
              LANDING_PAGE_ROLES.ABOUT_US,
            ].includes(p.role)
          }
          onReady={setPagesReady}
        />
        {config.showCostsPage && <SafariCosts onReady={setCostsReady} />}
        <OtherPages
          pagesFilterFn={(p) => [LANDING_PAGE_ROLES.TERMS, LANDING_PAGE_ROLES.COMMON_PAGES_SECOND].includes(p.role)}
          onReady={setPagesAfterCostsPageReady}
        />
      </div>
    </PDFDataContextProvider>
  );
}

export default PDFPreviewV2;
